/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'

import {
  EuiContextMenuPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPagination,
  EuiPopover,
} from '@elastic/eui'

import type { PaginationProps } from '@modules/cui/Table/types'

import MenuButton from './MenuButton'
import BillingYearMenuItem from './BillingYearMenuItem'

import type { FunctionComponent } from 'react'

type Props = PaginationProps & {
  billingYears: number[]
  onFilterByYear: (selectedYear?: number) => void
}

const TablePagination: FunctionComponent<Props> = ({
  activePage,
  billingYears,
  onChangePage,
  onFilterByYear,
  pageCount,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined)

  const getBillingYearItems = (years: number[]): JSX.Element[] => {
    const yearItems = years.map((year) => (
      <BillingYearMenuItem
        key={year}
        year={year}
        selectedYear={selectedYear}
        onSelectBillingYear={onSelectBillingYear}
      />
    ))

    if (years.length === 1) {
      return yearItems
    }

    return [
      <BillingYearMenuItem
        key='all'
        selectedYear={selectedYear}
        onSelectBillingYear={onSelectBillingYear}
      />,
    ].concat(yearItems)
  }

  const onSelectBillingYear = (year?: number): void => {
    setIsPopoverOpen(false)
    setSelectedYear(year)
    onFilterByYear(year)
  }

  const togglePopover = (): void => {
    setIsPopoverOpen(!isPopoverOpen)
  }

  const closePopover = (): void => {
    setIsPopoverOpen(false)
  }

  return (
    <EuiFlexGroup alignItems='center' justifyContent='spaceBetween'>
      <EuiFlexItem grow={false}>
        <EuiPopover
          id='billing-table-pagination.filter-popover'
          button={
            <MenuButton
              billingYears={billingYears}
              selectedYear={selectedYear}
              onClick={togglePopover}
            />
          }
          isOpen={isPopoverOpen}
          closePopover={closePopover}
          panelPaddingSize='none'
          anchorPosition='downLeft'
        >
          <EuiContextMenuPanel size='s' items={getBillingYearItems(billingYears)} />
        </EuiPopover>
      </EuiFlexItem>

      <EuiFlexItem grow={false}>
        <EuiPagination
          pageCount={pageCount}
          activePage={activePage}
          onPageClick={onChangePage}
          aria-label='Billing table pagination'
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default TablePagination
